import React from 'react';
import { Link } from 'react-router-dom';

import {
  useFeatureTrialsQuery,
  usePlansQuery,
} from 'src/api/queries/subscriptionQueries';
import {
  ENTERPRISE_CONTACT_URL,
  offeredPlans,
} from 'src/admin-pages/Subscription/planVisibility';

import styles from './FeatureTrialNotice.module.scss';

/**
 * "You're on a free trial of {label} — N days left" — rendered on a feature's
 * own page while an ACTIVE trial is what powers the access.
 *
 * Why: mid-trial the feature just works, and without this line the customer
 * has no way to see that the access is temporary (or when it ends) until the
 * expiry email lands. Renders nothing when there is no active trial for the
 * feature, so pages mount it unconditionally.
 *
 * The "keep it" half asks what there is to keep it WITH. Retiring every plan
 * code is how a deployment pauses self-serve (a pricing review), and then
 * "Upgrade to keep it" + "View plans" promises a purchase the app will not
 * take — the picker answers "there are no self-serve plans". So the CTA follows
 * the offer: plans if there are any, the Enterprise conversation if not.
 */
export const FeatureTrialNotice: React.FC<{
  featureCode: string;
  label: string;
}> = ({ featureCode, label }) => {
  const { data } = useFeatureTrialsQuery();
  // Pure catalog data, open to any authenticated caller and cached for an
  // hour, so this costs nothing in practice. Deliberately three-state: while
  // it is unknown the sentence simply stops after the countdown rather than
  // rendering one CTA and correcting itself a moment later.
  const { data: catalog, isLoading: catalogLoading } = usePlansQuery({ retry: false });
  const trial = data?.trials?.find(
    (t) => t.feature_code === featureCode && t.active,
  );
  if (!trial) return null;

  const canUpgrade = catalogLoading || !catalog
    ? undefined
    : offeredPlans(catalog.plans).length > 0;

  const days = trial.days_remaining;
  return (
    <div className={styles.notice} role="status" data-test={`trial-notice-${featureCode}`}>
      <span className={styles.badge}>Free trial</span>
      <span>
        You&apos;re trying {label} free
        {typeof days === 'number'
          ? ` — ${days} ${days === 1 ? 'day' : 'days'} left`
          : ''}
        .
        {canUpgrade === true && ' Upgrade to keep it after the trial.'}
        {canUpgrade === false && ' Talk to us about keeping it after the trial.'}
      </span>
      {canUpgrade === true && (
        <Link to="/admin/subscription/plans" className={styles.link}>
          View plans
        </Link>
      )}
      {canUpgrade === false && (
        <a
          href={ENTERPRISE_CONTACT_URL}
          target="_blank"
          // `noopener` is the load-bearing half: without it the opened page
          // gets a handle on this one through `window.opener` and can navigate
          // it elsewhere.
          rel="noopener noreferrer"
          className={styles.link}
        >
          Contact us
        </a>
      )}
    </div>
  );
};

export default FeatureTrialNotice;
